// ═══════════════════════════════════════════════════════════════════════════════════════════════
//  t337 · «ABRIR SU WHATSAPP»: LA FUNCIÓN COMÚN DE TODOS LOS BOTONES DE WHATSAPP DE UN PACIENTE (21-oct-2026)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «hay pacientes que, al pulsar el botón de WhatsApp de su perfil, no se abre correctamente: se queda en la
//  pestaña de WhatsApp pero sin abrir la conversación». Cada botón buscaba su conversación a su manera (la ficha con el
//  buscador de la bandeja y tragándose su error; la cita pegando un 34 delante de cualquier número). Ahora TODOS llaman
//  aquí, y aquí solo se pregunta al servidor (POST /api/conversaciones/abrir, backend/services/conversacionDePaciente),
//  que es quien sabe cuál es el móvil de la ficha, cuál es SU conversación (aunque esté cerrada, archivada o en otra
//  bandeja, y sin reabrirla), y la crea si no hay ninguna. Si no se puede, el porqué sale en un aviso: nunca un clic que
//  no hace nada.
//
//  QUIÉN LA USA: la ficha (la cabecera —components/ficha/CabeceraPaciente, vía `onWhatsApp`—, «Datos de contacto» y los
//  seguimientos: todos con el `abrirWhatsApp` de pages/Pacientes.jsx), la ventana de la cita (components/CitaModal) y la
//  cita nueva (pages/Agenda). Para llamarla desde otro sitio:
//      abrirWhatsAppPaciente({ api, msgError, pacienteId, telefono, nombre, toast, navegar, nuevaPestana })
//   · `api` (el cliente de services/api) y `msgError` (utils/errores) se PASAN, no se importan: así este fichero es lógica
//     pura y las guardias lo cargan de verdad, sin tener que doblarlo en cada pantalla que lo use. Sin `msgError`, el
//     porqué que escribió el servidor y, si no hay, un texto de respaldo;
//   · `pacienteId` (lo normal) y/o `telefono` (el de la cita, si la ficha no tiene móvil o no hay ficha);
//   · `navegar`: el `navigate` del enrutador; `nuevaPestana`: true para abrirla en otra pestaña donde las hay (la cita).
//  Devuelve { ok, id, como } (como = 'dentro' | 'pestana') o { ok: false, motivo }.
import { hayPestanas, irDentro } from './rutaInterna'
import { t, marca, tDato } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const RUTA_ABRIR = '/conversaciones/abrir'
export const destinoDeConversacion = (id) => '/comunicacion?conv=' + encodeURIComponent(String(id))

// El porqué de un fallo: el `msgError` de quien llama (utils/errores) o, sin él, lo que escribió el servidor.
function porqueDe(e, respaldo, msgError) {
  if (typeof msgError === 'function') { try { return msgError(e, respaldo) } catch (_) { /* el de respaldo */ } }
  const d = e && e.response && e.response.data
  if (d && typeof d.error === 'string' && d.error.trim()) return d.error.trim()
  return respaldo
}

function laVentana(ventana) {
  if (ventana) return ventana
  try { return (typeof globalThis !== 'undefined' && globalThis.window) || null } catch (_) { return null }
}

/** Lo que conviene saber al abrirla (no la bloquea): cerrada, archivada, de otra ficha, sin WhatsApp, nueva. */
export function avisosAlAbrir(r) {
  const a = []
  if (!r || typeof r !== 'object') return a
  if (r.creada) a.push(t('utils.abrirWhatsAppPaciente.no_tenia_conversacion_se_ha', 'No tenía conversación: se ha creado una nueva. Escríbele desde aquí.'))
  if (r.archivada) a.push(t('utils.abrirWhatsAppPaciente.esta_conversacion_esta_archivada_se', 'Esta conversación está archivada: se abre sin sacarla de Archivados.'))
  else if (r.estado === 'cerrada' || r.estado === 'cerrada_bot') a.push(t('utils.abrirWhatsAppPaciente.esta_conversacion_esta_cerrada_se', 'Esta conversación está cerrada: se abre sin reabrirla. Si le escribes, se reabre.'))
  if (r.deOtraFicha) a.push(t('utils.abrirWhatsAppPaciente.este_numero_es_tambien_el', 'Este número es también el de otra ficha{{x}}: la conversación está a su nombre. Comprueba a quién te diriges en el panel de la conversación.', { x: (r.historialDeOtraFicha ? t('utils.abrirWhatsAppPaciente.historial', ' (historial {{historialDeOtraFicha}})', { historialDeOtraFicha: r.historialDeOtraFicha }) : '') }))
  else if ((Number(r.fichasConEseNumero) || 0) > 1) a.push(t('utils.abrirWhatsAppPaciente.este_numero_lo_comparten_fichas', 'Este número lo comparten {{fichasConEseNumero}} fichas: elige a quién te diriges en el panel de la conversación.', { fichasConEseNumero: r.fichasConEseNumero }))
  if (r.sinWhatsapp) a.push(t('utils.abrirWhatsAppPaciente.ojo_en_su_ficha_consta', 'Ojo: en su ficha consta que este número no tiene WhatsApp.'))
  return a
}

/**
 * Abre la conversación de WhatsApp de un paciente (o de un teléfono suelto). Ver la cabecera.
 * La otra pestaña, si se pide, se abre EN EL CLIC (en blanco) y se le pone la dirección cuando contesta el servidor: abierta
 * después de esperar, el navegador la bloquea sin avisar («se pulsa y no pasa nada»).
 */
export async function abrirWhatsAppPaciente({ api, msgError = null, pacienteId = null, telefono = null, nombre = null, toast = null, navegar = null, nuevaPestana = false, ventana = null } = {}) {
  const w = laVentana(ventana)
  const avisoError = (m) => { try { toast && toast.error && toast.error(tDato(m)) } catch (_) { /* sin avisos */ } }
  if (!pacienteId && !String(telefono || '').trim()) {
    const motivo = marca('utils.abrirWhatsAppPaciente.no_hay_ficha_ni_telefono', 'No hay ficha ni teléfono al que escribir.')
    avisoError(motivo)
    return { ok: false, motivo }
  }
  let pestana = null
  if (nuevaPestana && hayPestanas(w)) {
    try { pestana = w && typeof w.open === 'function' ? w.open('', '_blank') : null } catch (_) { pestana = null }
  }
  const cerrarPestana = () => { try { if (pestana && typeof pestana.close === 'function') pestana.close() } catch (_) { /* ya cerrada */ } }
  let r = null
  try {
    const cuerpo = {}
    if (pacienteId) cuerpo.paciente_id = pacienteId
    if (String(telefono || '').trim()) cuerpo.telefono = String(telefono).trim()
    if (String(nombre || '').trim()) cuerpo.nombre_contacto = String(nombre).trim()
    const resp = await api.post(RUTA_ABRIR, cuerpo)
    r = resp && resp.data
  } catch (e) {
    cerrarPestana()
    const motivo = porqueDe(e, marca('utils.abrirWhatsAppPaciente.no_se_ha_podido_abrir', 'No se ha podido abrir la conversación'), msgError)
    avisoError(motivo)
    return { ok: false, motivo }
  }
  if (!r || !r.id) {
    cerrarPestana()
    const motivo = (r && (r.motivo || r.error)) || marca('utils.abrirWhatsAppPaciente.no_se_ha_podido_abrir', 'No se ha podido abrir la conversación')
    avisoError(motivo)
    return { ok: false, motivo }
  }
  const destino = destinoDeConversacion(r.id)
  // Todo lo que conviene saber, en UN aviso (varios seguidos se tapan unos a otros).
  const avisos = avisosAlAbrir(r)
  if (avisos.length) { try { toast && toast.info && toast.info(avisos.join(' '), 9000) } catch (_) { /* sin avisos */ } }
  if (pestana) {
    try {
      const origen = w && w.location && w.location.origin ? w.location.origin : ''
      pestana.location.href = origen + destino
      return { ok: true, id: r.id, como: 'pestana', respuesta: r }
    } catch (_) { cerrarPestana() }
  }
  const como = irDentro(destino, { navegar, ventana: w })
  return { ok: true, id: r.id, como: como || 'dentro', respuesta: r }
}

